import "./namespace.js";
import "#legacy/utilities/request_locale.js";
import "#legacy/shared/sparkle_uber_select.js";

/*
 *  Returns an object for displaying paginated comments on a physician's profile page
 *
 *  @param commentsEndpoint The url of the current physician's comments endpoint. Used to fetch the first page of results, after which the pagination on the first page will be used to determine the url of the desired page.
 *  @param surveyDetailsEndpoint The url of the current physician's survey details endpoint.
 *  @param container A selector or element that represents the container of the entire UI component.
 *  @param listElement A selector or element that represents the list of comments.
 *  @param orderSelect A selector or element that represents the ordering control of the UI component.
 *  @param surveyDetails A selector or element that represents the survey details section of the UI component.
 *  @param controls A selector or element that represents the controls section of the UI component.
 *  @param options Options hash
 *  @option options.showMore A boolean that when true will allow the list of articles to start collapsed and be expanded to show all. Defaults to true.
 */

window.Sparkle ??= {};
window.Sparkle.Physicians ??= {};

Sparkle.Physicians.Comments = class Comments {
  constructor(
    commentsEndpoint,
    surveyDetailsEndpoint,
    container,
    listElement,
    orderSelect,
    surveyDetails,
    controls,
    options,
  ) {
    this.registerListeners = this.registerListeners.bind(this);
    this.onSortChange = this.onSortChange.bind(this);
    this.onToggleShowMoreClick = this.onToggleShowMoreClick.bind(this);
    this.onPageLinkClick = this.onPageLinkClick.bind(this);
    this.renderSurveyDetails = this.renderSurveyDetails.bind(this);
    this.renderComments = this.renderComments.bind(this);
    this.fetchSurveyDetails = this.fetchSurveyDetails.bind(this);
    this.fetchComments = this.fetchComments.bind(this);
    this.fetchCommentsPage = this.fetchCommentsPage.bind(this);
    this.commentsElement = this.commentsElement.bind(this);
    this.hideComments = this.hideComments.bind(this);
    this.hide = this.hide.bind(this);
    this.show = this.show.bind(this);
    this.hideElement = this.hideElement.bind(this);
    this.showElement = this.showElement.bind(this);
    this.toggleShowMore = this.toggleShowMore.bind(this);
    this.getShowMoreButton = this.getShowMoreButton.bind(this);
    this.scrollToTop = this.scrollToTop.bind(this);
    this.calculateTopOffset = this.calculateTopOffset.bind(this);
    this.commentsEndpoint = commentsEndpoint;
    this.surveyDetailsEndpoint = surveyDetailsEndpoint;
    this.container = container;
    this.listElement = listElement;
    this.orderSelect = orderSelect;
    this.surveyDetails = surveyDetails;
    this.controls = controls;
    if (options == null) {
      options = {};
    }
    this.container = jQuery(this.container);
    this.listElement = jQuery(this.listElement);
    this.orderSelect = jQuery(this.orderSelect);
    this.surveyDetails = jQuery(this.surveyDetails);
    this.controls = jQuery(this.controls);
    this.options = jQuery.extend(
      {
        topOffset: this.calculateTopOffset,
        showMore: true,
      },
      options,
    );

    this.selectors = {
      scrollParent: "html,body",
      pagination: ".pagination a[href]",
      showMore: "[ref=toggle-show-more]",
      reviews: ".reviews",
      commentsContent: "[ref=reviews-content]",
    };

    this.cssClasses = { commentsCollapsed: "reviews--is-collapsed" };

    this.registerListeners();
    this.hide();
    this.hideElement(this.surveyDetails);
    this.hideComments();

    const detailsFetch = this.fetchSurveyDetails();
    const commentsFetch = this.fetchComments();

    detailsFetch.then(this.renderSurveyDetails).then((renderedDetails) => {
      if (renderedDetails) {
        return commentsFetch.then(this.renderComments);
      }
    }); // Only render comments if there are survey details
  }

  registerListeners() {
    this.orderSelect.change(this.onSortChange);
    this.listElement.on("click", this.selectors.pagination, this.onPageLinkClick);
    return this.listElement.on("click", this.selectors.showMore, this.onToggleShowMoreClick);
  }

  // EVENT HANDLERS

  onSortChange(e) {
    e.preventDefault();
    this.hideComments();
    return this.fetchComments().then(this.renderComments).then(this.scrollToTop);
  }

  onToggleShowMoreClick(e) {
    e.preventDefault();
    this.shouldShowMore = !this.shouldShowMore;
    this.fetchComments().then(this.renderComments).then(this.toggleShowMore);
    if (!this.shouldShowMore) {
      return this.scrollToTop();
    }
  }

  onPageLinkClick(e) {
    e.preventDefault();
    this.hideComments();
    return this.fetchCommentsPage(jQuery(e.currentTarget).attr("href"))
      .then(this.renderComments)
      .then(this.scrollToTop);
  }

  // RENDERING

  renderSurveyDetails(html) {
    this.surveyDetails.html(html);
    if (!html) {
      return;
    }
    this.show();
    return this.showElement(this.surveyDetails);
  }

  renderComments(html) {
    let content;
    if (html) {
      const parser = new DOMParser();
      const htmlDocument = parser.parseFromString(html, "text/html");
      content = htmlDocument.body.innerHTML; // ensure we strip head / body / meta tags
    } else {
      content = "";
    }
    this.listElement.html(content);
    this.toggleShowMore();
    if (!html) {
      return;
    }

    this.show();
    this.showElement(this.listElement);
    return this.controls.show();
  }

  // PRIVATE METHODS

  fetchSurveyDetails() {
    // Forward the page locale so the server renders these HTML payloads in the
    // visitor's language. A request without one is served in the default locale.
    const data = {};
    const locale = Sparkle.Utilities.requestLocale();
    if (locale) {
      data["locale"] = locale;
    }
    return jQuery.get(this.surveyDetailsEndpoint, data, null, "html");
  }

  fetchComments(options) {
    return this.fetchCommentsPage(this.commentsEndpoint, {
      page: 1,
      order: this.orderSelect.val(),
    });
  }

  fetchCommentsPage(url, params) {
    const data = {
      order: this.orderSelect.val(),
      api: "snippet",
    };
    if (params != null ? params["page"] : undefined) {
      data["page"] = params["page"];
    }
    if (params && params["order"]) {
      data["order"] = params["order"];
    }
    const locale = Sparkle.Utilities.requestLocale();
    if (locale) {
      data["locale"] = locale;
    }
    return jQuery.get(url, data, null, "html");
  }

  commentsElement() {
    return jQuery(this.listElement.find(this.selectors.commentsContent).get(0) || this.listElement);
  }

  hideComments() {
    return this.hideElement(this.listElement);
  }

  hide() {
    this.container.hide();
    if (this.options.onHide != null) {
      return this.options.onHide();
    }
  }

  show() {
    this.container.show();
    if (this.options.onShow != null) {
      return this.options.onShow();
    }
  }

  hideElement($element) {
    return $element.animate({ opacity: 0.1 }, "fast");
  }

  showElement($element) {
    return $element.animate({ opacity: 1 }, "fast");
  }

  toggleShowMore() {
    const button = this.getShowMoreButton();
    const text = this.shouldShowMore ? button.data("less-text") : button.data("more-text");
    button.text(text);
    return this.listElement
      .find(this.selectors.reviews)
      .toggleClass(this.cssClasses.commentsCollapsed, !this.shouldShowMore);
  }

  getShowMoreButton() {
    return this.listElement.find(this.selectors.showMore);
  }

  scrollToTop() {
    this.container[0].scrollIntoView({ behavior: "smooth", block: "start" });
    return Promise.resolve();
  }

  calculateTopOffset() {
    return this.listElement.offset().top;
  }
};
